import { useEffect, useState } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { LABS } from "@/lib/labs/registry";
import type { LabModule } from "@/lib/labs/types";
import { LabPlayer } from "@/components/labs/lab-player";
import { SimulationGate } from "@/components/billing/simulation-gate";

export const Route = createFileRoute("/labs/$labId")({
  loader: ({ params }) => {
    const meta = LABS.find((l) => l.id === params.labId);
    if (!meta) throw notFound();
    const { load: _load, ...rest } = meta;
    return rest;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Experiment not found" }, { name: "robots", content: "noindex" }] };
    }
    const title = `${loaderData.title} — Sim Labs | The Quantum Minds`;
    return {
      ...seo({
        title,
        description: loaderData.blurb,
        path: `/labs/${params.labId}`,
        type: "article",
      }),
      scripts: [
        breadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Sim Labs", path: "/labs" },
          { name: loaderData.title, path: `/labs/${params.labId}` },
        ]),
      ],
    };
  },
  component: LabPage,
  notFoundComponent: LabNotFound,
});

function LabNotFound() {
  return (
    <div className="page-shell px-5 lg:px-8 py-16 text-center">
      <p className="font-semibold">That experiment doesn&apos;t exist</p>
      <Link to="/labs" className="mt-3 inline-block text-sm font-semibold text-primary">
        Back to Sim Labs
      </Link>
    </div>
  );
}

function LabPage() {
  const meta = Route.useLoaderData();
  const [lab, setLab] = useState<LabModule<unknown> | null>(null);

  useEffect(() => {
    let alive = true;
    setLab(null);
    LABS.find((l) => l.id === meta.id)
      ?.load()
      .then((mod) => {
        if (alive) setLab(mod.default as unknown as LabModule<unknown>);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [meta.id]);

  return (
    <div className="page-shell px-5 lg:px-8 pb-24 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/labs"
          aria-label="Back to Sim Labs"
          className="glass grid size-9 shrink-0 place-items-center rounded-full transition-transform active:scale-95"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            {meta.group} · {meta.chapter}
          </p>
          <h1 className="truncate text-[19px] font-extrabold tracking-tight">{meta.title}</h1>
        </div>
      </div>

      <div className="mt-5">
        <SimulationGate>
        {lab ? (
          <LabPlayer lab={lab} />
        ) : (
          <div className="space-y-3">
            <div className="glass animate-shimmer h-[300px] rounded-[22px]" />
            <div className="glass animate-shimmer h-32 rounded-[20px]" />
          </div>
        )}
        </SimulationGate>
      </div>
    </div>
  );
}